import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { NAV_ITEMS } from "./constants";

// ── Where each sidebar item actually goes ───────────────────────────────────
// BUG FIX: every item here used to fall through to `path || "#"` because
// NAV_ITEMS never defines a `path` field — so every single link (Orders,
// Tracking, Drivers, Vehicles, Map, Alerts, Messages, Reports, Documents,
// Settings) called `navigate("#")`, which goes nowhere. That's the whole
// "none of the sidebar works" bug.
//
// This app doesn't have separate routed pages for most of these yet — only
// Dashboard (DispatcherOverview) and Orders (DispatcherOrders) really
// exist as their own pages. So:
//   - "route": a real dedicated page → navigate there directly.
//   - "tab":   something DispatcherOverview already renders internally
//              (Alerts/Messages/Chat panels) → deep-link into the
//              dashboard with a `?tab=` param it reads on load.
//   - null:    no page exists yet → don't pretend to navigate; show a
//              real "not built yet" toast instead of silently doing
//              nothing, using the same pop-up notification system the
//              dashboard already has for chat messages.
// If your router uses a different URL for the Orders page than
// "/dispatcher-orders", update ROUTE_FOR.orders below to match.
const ROUTE_FOR = {
  dashboard: { type: "route", path: "/dispatcher-dashboard" },
  orders:    { type: "route", path: "/dispatcher-orders" },
  tracking:  { type: "route", path: "/dispatcher-dashboard" }, // live map is already the dashboard's main view
  map:       { type: "route", path: "/dispatcher-dashboard" },
  alerts:    { type: "tab", tab: "alerts" },
  messages:  { type: "tab", tab: "messages" },
  drivers:   { type: "soon" },
  vehicles:  { type: "soon" },
  reports:   { type: "soon" },
  documents: { type: "soon" },
  settings:  { type: "soon" },
};

export default function Sidebar({ sidebarOpen, setSidebarOpen, handleLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Strip trailing slashes to match nav configs easily
  const currentPath = location.pathname.replace(/\/$/, "") || "/dispatcher-dashboard";
  
  // BUG FIX: isActive below used to only compare pathname, so every item
  // whose target happened to be "/dispatcher-dashboard" (Dashboard,
  // Tracking, Map, Alerts, Messages — 5 different keys all routing to the
  // same page) lit up simultaneously, regardless of which tab was actually
  // open. Now it also checks the `?tab=` query param so exactly one item
  // is active at a time, matching where you actually are.
  const currentTab = new URLSearchParams(location.search).get("tab");

  const goTo = (key, label) => {
    const target = ROUTE_FOR[key];
    if (!target || target.type === "route") {
      navigate(target?.path || "/dispatcher-dashboard");
    } else if (target.type === "tab") {
      navigate(`/dispatcher-dashboard?tab=${target.tab}`);
    } else {
      // No page built yet — say so, don't pretend the click did something.
      navigate(`/dispatcher-dashboard?soon=${encodeURIComponent(label)}`);
    }
    setSidebarOpen(false);
  };

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed lg:relative z-50 lg:z-auto top-0 left-0 h-full w-56 flex-shrink-0 flex flex-col transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        style={{ background: "#1a2744" }}
      >
        <div className="flex items-center gap-2.5 px-4 py-4 border-b border-white/10 flex-shrink-0">
          <div className="w-9 h-9 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 17H5a2 2 0 01-2-2V5a2 2 0 012-2h11a2 2 0 012 2v3m-1 11a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2h8z"/>
            </svg>
          </div>
          <span className="font-bold text-white text-base tracking-wide">DISPATCHER</span>
          <button onClick={() => setSidebarOpen(false)} className="ml-auto lg:hidden text-white/50 hover:text-white">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <nav className="flex-1 px-2 py-3 overflow-y-auto space-y-0.5 custom-scrollbar">
          <Link
            to="/dispatcher-dashboard"
            onClick={() => setSidebarOpen(false)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-all mb-1"
          >
            <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <span className="truncate">Live Chat &amp; GPS Tracker</span>
          </Link>
          <div className="border-t border-white/10 my-1" />
          
          {NAV_ITEMS.map(({ key, label, icon, badge }) => {
            const target = ROUTE_FOR[key];
            // Only these keys represent a genuinely distinct destination.
            // "tracking" and "map" deliberately alias straight to the
            // Dashboard (there's no separate page for them yet — see
            // ROUTE_FOR above) — they stay clickable, they just never show
            // as "active" themselves since Dashboard already owns that
            // indicator for the same page.
            let isActive = false;
            
            if (key === "dashboard") {
              isActive = currentPath === "/dispatcher-dashboard" && !currentTab;
            } else if (key === "tracking" || key === "map") {
              // Explicitly prevent these from evaluating as active 
              // since they route to the exact same URL as dashboard
              isActive = false;
            } else if (target?.type === "route") {
              isActive = currentPath === target.path;
            } else if (target?.type === "tab") {
              isActive = currentPath === "/dispatcher-dashboard" && currentTab === target.tab;
            }

            return (
              <button
                key={key}
                onClick={() => goTo(key, label)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "text-gray-300 hover:bg-white/10 hover:text-white"
                }`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                  {icon.split(" M").map((seg, i) => (
                    <path key={i} strokeLinecap="round" strokeLinejoin="round" d={i === 0 ? seg : "M" + seg}/>
                  ))}
                </svg>
                <span className="truncate">{label}</span>
                {badge && (
                  <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0">
                    {badge}
                  </span>
                )}
              </button>
            );
          })} 
          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-all border border-transparent hover:border-red-500/20 mt-4"
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="truncate">Logout</span>
          </button>
        </nav>
      </aside>
    </>
  );
}